import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Navigate, useNavigate } from "react-router-dom";
import { 
  BookOpen, Map, Users, Skull, Sparkles, Castle, ShieldCheck, Swords, 
  Scroll, Wand2, Dices, FileText, Package, UserCircle2, Globe, 
  Clock, Download, Share2, ChevronRight, Crown, Star, Check, ArrowRight,
  Layers, Flame, GitBranch, Eye, Image as ImageIcon, Zap, X, ChevronLeft
} from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";

// Real campaign screenshots — used in showcase gallery + inline feature previews
const CAMPAIGN_SHOTS = [
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/uutjursf_Picture1.png",  caption: "Campaign Dashboard" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/ljcniyvs_Picture2.png",  caption: "Story Chapters & Rich Text" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/8xlf00g4_Picture3.png",  caption: "Session Notes" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/l2iczg3k_Picture4.png",  caption: "Virtual Tabletop — live combat" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/wr7ekou8_Picture5.png",  caption: "Dynamic Lighting & Vision" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/jynwf6o6_Picture6.png",  caption: "Fog of War brush" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/5wn4sawg_Picture7.png",  caption: "Spell Templates on the Grid" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/t7kzxftr_Picture8.png",  caption: "Encounter Builder" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/qi5xi89l_Picture9.png",  caption: "Interactive World Map" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/b8oywzdx_Picture10.png", caption: "Location Pins & Details" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/8b58l1kd_Picture11.png", caption: "Locations & Buildings" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/nyl6x5w3_Picture12.png", caption: "NPC Profile" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/xkood1ud_Picture13.png", caption: "Bestiary Stat Block" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/p2v7v5qz_Picture14.png", caption: "Items & Inventory" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/8ilt2o8k_Picture15.png", caption: "Character Sheet — parchment layout" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/otxe1rbf_Picture16.png", caption: "Player Wiki tome" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/nilhycq4_Picture17.png", caption: "Family Trees" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/x3lgwuvr_Picture18.png", caption: "Multi-Tab Workspace" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/nc0o7luo_Picture19.png", caption: "DM Screen quick-reference" },
  { url: "https://customer-assets.emergentagent.com/job_fantasy-lore-vault/artifacts/mp6r5x7r_Picture20.png", caption: "Campaign Export" },
];

const LandingPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [gridOpen, setGridOpen] = useState(false);

  const openLightbox  = useCallback((i) => setLightboxIndex(i), []);
  const closeLightbox = useCallback(()  => setLightboxIndex(null), []);
  const prevShot      = useCallback(()  => setLightboxIndex((i) => (i === null ? i : (i - 1 + CAMPAIGN_SHOTS.length) % CAMPAIGN_SHOTS.length)), []);
  const nextShot      = useCallback(()  => setLightboxIndex((i) => (i === null ? i : (i + 1) % CAMPAIGN_SHOTS.length)), []);

  useEffect(() => {
    if (lightboxIndex === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape")     closeLightbox();
      if (e.key === "ArrowLeft")  prevShot();
      if (e.key === "ArrowRight") nextShot();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, closeLightbox, prevShot, nextShot]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-[#09090b] text-[#F3F4F6] overflow-hidden" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="landing-page">

      {/* ===== LIGHTBOX ===== */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center p-2 sm:p-4"
          onClick={closeLightbox}
          data-testid="showcase-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            data-testid="lightbox-close"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-[#F3F4F6] flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prevShot(); }}
            data-testid="lightbox-prev"
            className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-[#D4AF37]/80 hover:text-[#09090b] text-[#F3F4F6] flex items-center justify-center transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); nextShot(); }}
            data-testid="lightbox-next"
            className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-[#D4AF37]/80 hover:text-[#09090b] text-[#F3F4F6] flex items-center justify-center transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <div className="w-full h-full flex flex-col items-center justify-center gap-3" onClick={(e) => e.stopPropagation()}>
            <img
              src={CAMPAIGN_SHOTS[lightboxIndex].url}
              alt=""
              className="max-h-[92vh] w-auto max-w-[95vw] rounded-xl border border-white/10 shadow-[0_20px_80px_-20px_rgba(212,175,55,0.35)] object-contain"
              data-testid="lightbox-image"
            />
            <div className="flex items-center gap-3 text-center">
              <span className="text-xs text-[#A1A1AA] uppercase tracking-[0.2em]">{lightboxIndex + 1} / {CAMPAIGN_SHOTS.length}</span>
            </div>
          </div>
        </div>
      )}

      {/* ===== HEADER ===== */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#09090b]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="NaturalOne Chronicles" className="w-9 h-9 rounded-lg object-contain" />
            <span className="text-lg tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700 }}>
              NaturalOne<span className="text-[#D4AF37]">-Chronicles</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => navigate('/login?admin=true')}
              variant="ghost"
              data-testid="admin-login-button"
              className="text-[#A1A1AA] hover:text-[#F3F4F6] rounded-full text-sm gap-1.5 hidden sm:flex"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> Admin
            </Button>
            <Button 
              onClick={() => navigate('/login')}
              data-testid="email-login-button"
              className="bg-transparent text-[#F3F4F6] font-semibold px-5 py-2 rounded-full border border-white/20 hover:bg-white/5 hover:border-[#D4AF37]/50 transition-all duration-300 text-sm"
            >
              Sign In
            </Button>
            <Button 
              onClick={() => navigate('/login')}
              data-testid="login-button"
              className="bg-[#D4AF37] text-[#09090b] font-bold px-5 py-2 rounded-full hover:bg-[#FDE047] transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.3)] hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] text-sm"
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* ===== HERO SECTION ===== */}
      <section className="relative min-h-screen flex items-center pt-16">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b63c2fc7-d0b1-44b1-9fb4-5df15a3eac55/images/2cf0bbda612b80129b1b772bd1db373a0d918edee90eb49867387a7c65a3db66.png"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09090b] via-[#09090b]/85 to-[#09090b]" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 w-full">
          <div className="max-w-4xl mx-auto text-center">
            {/* Big centered logo — the focal point on first load */}
            <div className="flex justify-center mb-8" data-testid="hero-logo-wrapper">
              <div className="relative">
                {/* Soft gold glow behind the logo */}
                <div
                  className="absolute inset-0 rounded-full blur-3xl opacity-60"
                  style={{
                    background: "radial-gradient(circle, rgba(212,175,55,0.45) 0%, rgba(212,175,55,0) 70%)",
                    transform: "scale(1.4)",
                  }}
                />
                <img
                  src="/logo.png"
                  alt="NaturalOne-Chronicles"
                  className="relative w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 object-contain drop-shadow-[0_0_40px_rgba(212,175,55,0.35)]"
                  data-testid="hero-logo"
                />
              </div>
            </div>

            <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#D4AF37] mb-4" data-testid="hero-overline">
              The Dungeon Master's Codex
            </p>
            <h1
              className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-[#F3F4F6] mb-6 leading-[1.05]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="hero-title"
            >
              Your Campaign.<br/>
              <span className="text-[#D4AF37]">Immortalized.</span>
            </h1>
            <p className="text-base lg:text-lg leading-relaxed text-[#A1A1AA] mb-4 max-w-2xl mx-auto">
              Plan the story. Run the table. Share the world with your party.
            </p>
            <p className="text-sm lg:text-base leading-relaxed text-[#71717a] mb-10 max-w-2xl mx-auto">
              Built by a Dungeon Master, for Dungeon Masters &mdash; every NPC, creature, map, session note,
              spell effect, and dice roll interconnected and ready the moment the dice hit wood.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/login')}
                data-testid="cta-start-campaign"
                className="bg-[#D4AF37] text-[#09090b] font-bold px-8 py-6 rounded-full hover:bg-[#FDE047] transition-all duration-300 shadow-[0_0_30px_rgba(212,175,55,0.45)] hover:shadow-[0_0_40px_rgba(212,175,55,0.7)] text-lg"
              >
                Begin Your Chronicle <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                onClick={() => navigate('/login')}
                data-testid="cta-email-signup"
                className="bg-transparent text-[#F3F4F6] font-semibold px-8 py-6 rounded-full border border-white/20 hover:bg-white/5 hover:border-[#D4AF37]/50 transition-all duration-300 text-lg"
              >
                Sign Up with Email
              </Button>
            </div>
            {/* What's new chip row */}
            <div className="mt-12 flex flex-wrap items-center justify-center gap-2 text-xs" data-testid="hero-whats-new">
              <span className="px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] font-bold uppercase tracking-wider">Now Shipping</span>
              <span className="text-[#A1A1AA]">
                Dynamic Lighting Engine &nbsp;·&nbsp; Per-Token Vision (Darkvision / Torch) &nbsp;·&nbsp; Wall Collision &nbsp;·&nbsp; Vision Mode Toggle &nbsp;·&nbsp; Multi-Tab Workspace
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SHOWCASE — real campaign screenshots ===== */}
      <section className="relative py-20 sm:py-28 border-t border-white/5" data-testid="showcase-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full opacity-[0.08]"
               style={{ background: "radial-gradient(circle, rgba(212,175,55,0.9) 0%, rgba(212,175,55,0) 65%)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10">
            <div>
              <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
                See it in action
              </p>
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#F3F4F6]"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
                data-testid="showcase-title"
              >
                Real campaigns, running right now
              </h2>
              <p className="text-sm sm:text-base text-[#A1A1AA] mt-4 max-w-2xl">
                Not mockups &mdash; these are screenshots from live tables using NaturalOne-Chronicles.
                Click any tile to zoom in.
              </p>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA]">
              <span className="w-8 h-px bg-[#D4AF37]/50" />
              {CAMPAIGN_SHOTS.length} screens
            </div>
          </div>

          {/* Horizontal image marquee — auto-scrolls, pauses on hover, click to zoom */}
          <div className="relative -mx-6 lg:-mx-8 overflow-hidden group/marquee" data-testid="showcase-marquee">
            {/* edge fades */}
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 sm:w-24 z-10 bg-gradient-to-r from-[#09090b] to-transparent" />
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 sm:w-24 z-10 bg-gradient-to-l from-[#09090b] to-transparent" />
            <div className="flex animate-marquee-slow group-hover/marquee:[animation-play-state:paused] whitespace-nowrap py-4">
              {[...Array(2)].map((_, rep) => (
                <div key={rep} className="flex items-center gap-5 px-2.5" aria-hidden={rep === 1}>
                  {CAMPAIGN_SHOTS.map((shot, i) => (
                    <button
                      key={`${rep}-${shot.url}`}
                      type="button"
                      onClick={() => openLightbox(i)}
                      data-testid={rep === 0 ? `showcase-marquee-tile-${i}` : undefined}
                      className="group/tile relative flex-shrink-0 w-[280px] sm:w-[340px] md:w-[400px] aspect-video overflow-hidden rounded-xl border border-white/10 bg-[#18181b] transition-all duration-300 hover:border-[#D4AF37]/60 hover:shadow-[0_10px_40px_-10px_rgba(212,175,55,0.5)] hover:z-10"
                    >
                      <img
                        src={shot.url}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover/tile:scale-[1.04]"
                      />
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Toggle: view all in grid */}
          <div className="flex justify-center mt-8">
            <Button
              onClick={() => setGridOpen((v) => !v)}
              data-testid="showcase-grid-toggle"
              className="bg-transparent text-[#F3F4F6] font-semibold px-6 py-2.5 rounded-full border border-white/20 hover:bg-white/5 hover:border-[#D4AF37]/50 transition-all text-sm gap-2"
            >
              {gridOpen ? "Hide gallery" : `View all ${CAMPAIGN_SHOTS.length} in grid`}
              <ChevronRight className={`w-4 h-4 transition-transform ${gridOpen ? "rotate-90" : ""}`} />
            </Button>
          </div>

          {/* Expandable grid */}
          {gridOpen && (
            <div
              className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
              data-testid="showcase-grid"
            >
              {CAMPAIGN_SHOTS.map((shot, i) => (
                <button
                  key={shot.url}
                  type="button"
                  onClick={() => openLightbox(i)}
                  data-testid={`showcase-tile-${i}`}
                  className="group relative aspect-video overflow-hidden rounded-lg border border-white/5 bg-[#18181b] transition-all duration-300 hover:border-[#D4AF37]/40 hover:-translate-y-0.5 hover:shadow-[0_10px_40px_-10px_rgba(212,175,55,0.35)]"
                >
                  <img
                    src={shot.url}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ===== MARQUEE RIBBON ===== */}
      <div className="border-y border-white/10 bg-[#09090b]/60 backdrop-blur-xl py-5 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, rep) => (
            <div key={rep} className="flex items-center gap-8 mx-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {["Dynamic Lighting", "Per-Token Vision", "Wall Collision", "Vision Mode Toggle", "Virtual Tabletop", "Fog of War", "Spell Effects", "Multi-Tab Workspace", "Story Chapters", "Interactive Maps", "NPCs", "334-Creature Bestiary", "Locations", "Buildings", "Lore", "42 Deities", "Items", "Combat Tracker", "Family Trees", "Handouts", "AI Oracle", "AI Image Generation", "Session Logs", "Campaign Export"].map((item, i) => (
                <span key={i} className="text-lg text-[#A1A1AA] tracking-wide flex items-center gap-3">
                  <span className="text-[#D4AF37]">{item}</span>
                  <Star className="w-3 h-3 text-[#D4AF37]/40" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ===== FEATURES BENTO GRID ===== */}
      <section className="py-24 sm:py-32" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Everything You Need</p>
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#F3F4F6] mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Forge Your World
          </h2>
          <p className="text-base lg:text-lg leading-relaxed text-[#A1A1AA] mb-16 max-w-2xl">
            Every tool a Dungeon Master needs, from session zero to the final boss. 
            Build, track, and share your campaign with your players.
          </p>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            
            {/* Worldbuilding — Large card */}
            <div className="md:col-span-8 relative overflow-hidden rounded-2xl bg-[#18181b] border border-white/5 transition-all duration-300 hover:border-[#D4AF37]/30 hover:-translate-y-1 group" data-testid="feature-worldbuilding">
              <div className="absolute inset-0 opacity-[0.18] group-hover:opacity-25 transition-opacity">
                <img src={CAMPAIGN_SHOTS[1].url} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-br from-[#18181b] via-[#18181b]/40 to-transparent" />
              </div>
              <div className="relative p-8 sm:p-10">
                <div className="flex items-center gap-3 mb-4">
                  <Globe className="w-6 h-6 text-[#D4AF37]" />
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#D4AF37]">Campaign Worldbuilding</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#F3F4F6] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Every Detail, Connected
                </h3>
                <p className="text-[#A1A1AA] leading-relaxed max-w-lg mb-6">
                  Write story chapters with rich text. Create locations with buildings, menus, and histories. 
                  Link NPCs to their homes, items to their owners. Wiki-style cross-linking makes your world feel alive.
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Chapters", "Locations", "Buildings", "Lore", "Deities", "Rich Text Editor"].map(tag => (
                    <span key={tag} className="text-xs bg-white/5 border border-white/10 text-[#A1A1AA] px-3 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* AI Oracle */}
            <div className="md:col-span-4 relative overflow-hidden rounded-2xl bg-[#18181b] border border-white/5 transition-all duration-300 hover:border-[#D4AF37]/30 hover:-translate-y-1 group" data-testid="feature-ai-oracle">
              <div className="absolute inset-0 opacity-30 group-hover:opacity-40 transition-opacity">
                <img src="https://static.prod-images.emergentagent.com/jobs/b63c2fc7-d0b1-44b1-9fb4-5df15a3eac55/images/e463573526800db0e4bd3ee7c07106e3947d3eb731e2c2173437316835ee55fb.png" alt="" className="w-full h-full object-cover" />
              </div>
              <div className="relative p-8 sm:p-10 h-full flex flex-col justify-end">
                <Wand2 className="w-6 h-6 text-[#D4AF37] mb-3" />
                <h3 className="text-xl font-medium text-[#F3F4F6] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>AI Oracle</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">
                  Stuck for ideas? Ask the Oracle. Generate NPC names, quest hooks, tavern descriptions, 
                  and encounter ideas — all with a fantasy flair.
                </p>
              </div>
            </div>

            {/* Virtual Tabletop — Full-width hero card */}
            <div className="md:col-span-12 relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1c1917] via-[#18181b] to-[#09090b] border border-[#D4AF37]/20 transition-all duration-300 hover:border-[#D4AF37]/50 hover:-translate-y-1 group" data-testid="feature-vtt">
              <div className="relative grid md:grid-cols-2 gap-8 p-8 sm:p-12">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="bg-[#D4AF37] text-[#09090b] text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm">New</span>
                    <Layers className="w-6 h-6 text-[#D4AF37]" />
                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#D4AF37]">Virtual Tabletop</span>
                  </div>
                  <h3 className="text-3xl sm:text-4xl font-medium tracking-tight text-[#F3F4F6] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Your table, your tools, your world
                  </h3>
                  <p className="text-[#A1A1AA] leading-relaxed max-w-lg mb-6">
                    Built by a DM, for DMs. Drop any NPC or creature onto the map, paint fog of war
                    exactly where the party can see, cast area spells with a click, and track every
                    HP and initiative score — all synced live to your players with no separate app,
                    no logins, no friction.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {["Real-Time Sync", "Drag-and-Drop Tokens", "Dynamic Lighting", "Wall Collision", "Spell Effects (Area / Line / Cone)", "Pinch-to-Zoom"].map(tag => (
                      <span key={tag} className="text-xs bg-white/5 border border-[#D4AF37]/20 text-[#F3F4F6] px-3 py-1 rounded-full">{tag}</span>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { icon: Eye,       label: "Fog of War" },
                      { icon: Flame,     label: "Spell Effects" },
                      { icon: Swords,    label: "Combat Tracker" },
                      { icon: GitBranch, label: "Family Trees" },
                      { icon: Share2,    label: "Live Handouts" },
                      { icon: ImageIcon, label: "AI Token Art" },
                    ].map(({ icon: Icon, label }) => (
                      <div key={label} className="bg-[#09090b]/60 border border-white/5 rounded-lg p-2.5 flex items-center gap-2">
                        <Icon className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
                        <p className="text-[11px] font-semibold text-[#F3F4F6] leading-tight">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 content-center">
                  <button
                    type="button"
                    onClick={() => openLightbox(3)}
                    data-testid="feature-vtt-shot-1"
                    className="col-span-2 group/img relative overflow-hidden rounded-xl border border-[#D4AF37]/20 aspect-video bg-black/40"
                  >
                    <img src={CAMPAIGN_SHOTS[3].url} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover/img:scale-[1.03]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openLightbox(4)}
                    data-testid="feature-vtt-shot-2"
                    className="group/img relative overflow-hidden rounded-xl border border-[#D4AF37]/20 aspect-video bg-black/40"
                  >
                    <img src={CAMPAIGN_SHOTS[4].url} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover/img:scale-[1.03]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openLightbox(6)}
                    data-testid="feature-vtt-shot-3"
                    className="group/img relative overflow-hidden rounded-xl border border-[#D4AF37]/20 aspect-video bg-black/40"
                  >
                    <img src={CAMPAIGN_SHOTS[6].url} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover/img:scale-[1.03]" />
                  </button>
                </div>
              </div>
            </div>

            {/* Combat Tracker */}
            <div className="md:col-span-6 relative overflow-hidden rounded-2xl bg-[#18181b] border border-white/5 transition-all duration-300 hover:border-[#D4AF37]/30 hover:-translate-y-1" data-testid="feature-combat">
              <div className="absolute inset-0 opacity-[0.18]">
                <img src={CAMPAIGN_SHOTS[7].url} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#18181b] via-[#18181b]/60 to-transparent" />
              </div>
              <div className="relative p-8 sm:p-10">
                <div className="flex items-center gap-3 mb-4">
                  <Swords className="w-6 h-6 text-[#D4AF37]" />
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#D4AF37]">Combat & Dice</span>
                </div>
                <h3 className="text-2xl font-medium text-[#F3F4F6] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Roll Initiative
                </h3>
                <p className="text-[#A1A1AA] leading-relaxed mb-4">
                  Full combat tracker with initiative order, HP, armour class, and conditions. 
                  Floating dice roller always at hand. Track rounds and turns seamlessly.
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Initiative Tracker", "HP & AC", "Conditions", "Dice Roller", "Round Counter"].map(tag => (
                    <span key={tag} className="text-xs bg-white/5 border border-white/10 text-[#A1A1AA] px-3 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Interactive Maps */}
            <div className="md:col-span-6 relative overflow-hidden rounded-2xl bg-[#18181b] border border-white/5 transition-all duration-300 hover:border-[#D4AF37]/30 hover:-translate-y-1" data-testid="feature-maps">
              <div className="absolute inset-0 opacity-[0.18]">
                <img src={CAMPAIGN_SHOTS[8].url} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-l from-[#18181b] via-[#18181b]/60 to-transparent" />
              </div>
              <div className="relative p-8 sm:p-10">
                <div className="flex items-center gap-3 mb-4">
                  <Map className="w-6 h-6 text-[#D4AF37]" />
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#D4AF37]">Interactive Maps</span>
                </div>
                <h3 className="text-2xl font-medium text-[#F3F4F6] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Pin Your World
                </h3>
                <p className="text-[#A1A1AA] leading-relaxed mb-4">
                  Upload your maps, then click to place pins. Each pin links to a location with buildings, 
                  NPCs, and descriptions. Pan, zoom, and explore your world like a true cartographer.
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Custom Maps", "Clickable Pins", "11 Location Types", "Pan & Zoom", "Auto-Link Locations"].map(tag => (
                    <span key={tag} className="text-xs bg-white/5 border border-white/10 text-[#A1A1AA] px-3 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* People & Creatures — 4 col */}
            <div className="md:col-span-4 rounded-2xl bg-[#18181b] border border-white/5 p-8 transition-all duration-300 hover:border-[#D4AF37]/30 hover:-translate-y-1" data-testid="feature-people">
              <Users className="w-6 h-6 text-[#D4AF37] mb-4" />
              <h3 className="text-xl font-medium text-[#F3F4F6] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Players, NPCs & Bestiary</h3>
              <p className="text-sm text-[#A1A1AA] leading-relaxed mb-4">
                Track player characters with D&D Beyond import. Create NPCs with professions — shopkeepers, blacksmiths, innkeepers. 
                Catalog your bestiary with challenge ratings.
              </p>
              <div className="space-y-2">
                {[
                  { icon: Users, label: "Player Characters" },
                  { icon: UserCircle2, label: "NPCs with Professions" },
                  { icon: Skull, label: "Bestiary & Creatures" },
                  { icon: Package, label: "Items & Inventory" }
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                    <Icon className="w-4 h-4 text-[#D4AF37]/60" />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Session Tools — 4 col */}
            <div className="md:col-span-4 rounded-2xl bg-[#18181b] border border-white/5 p-8 transition-all duration-300 hover:border-[#D4AF37]/30 hover:-translate-y-1" data-testid="feature-sessions">
              <Clock className="w-6 h-6 text-[#D4AF37] mb-4" />
              <h3 className="text-xl font-medium text-[#F3F4F6] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Session Tools</h3>
              <p className="text-sm text-[#A1A1AA] leading-relaxed mb-4">
                Log every session with timestamped entries. Link sessions to story chapters. 
                Import notes from Word or PDF. DM Screen gives you quick-reference at your fingertips.
              </p>
              <div className="space-y-2">
                {[
                  { icon: Clock, label: "Session Logs" },
                  { icon: FileText, label: "Notes & Doc Import" },
                  { icon: Dices, label: "DM Screen" },
                  { icon: Download, label: "Export PDF/DOCX" }
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                    <Icon className="w-4 h-4 text-[#D4AF37]/60" />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sharing — 4 col */}
            <div className="md:col-span-4 rounded-2xl bg-[#18181b] border border-white/5 p-8 transition-all duration-300 hover:border-[#D4AF37]/30 hover:-translate-y-1" data-testid="feature-sharing">
              <Share2 className="w-6 h-6 text-[#D4AF37] mb-4" />
              <h3 className="text-xl font-medium text-[#F3F4F6] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Campaign Sharing</h3>
              <p className="text-sm text-[#A1A1AA] leading-relaxed mb-4">
                Invite players with a link. Control visibility — show players only what they've discovered. 
                DMs see everything, players see what you reveal.
              </p>
              <div className="space-y-2">
                {[
                  { icon: Share2, label: "Invite Links" },
                  { icon: ShieldCheck, label: "DM / Player Roles" },
                  { icon: Globe, label: "Visibility Controls" },
                  { icon: Users, label: "Party Management" }
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                    <Icon className="w-4 h-4 text-[#D4AF37]/60" />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section className="py-24 sm:py-32 relative" data-testid="pricing-section">
        <div className="absolute inset-0 opacity-[0.08]">
          <img src="https://images.pexels.com/photos/29672206/pexels-photo-29672206.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Pricing</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#F3F4F6] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Choose Your Path
            </h2>
            <p className="text-base text-[#A1A1AA] max-w-lg mx-auto">
              Start free. Upgrade when you're ready for more power.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto items-start">
            {/* Commoner */}
            <div className="rounded-2xl bg-[#18181b] border border-white/5 p-8 transition-all duration-300 hover:border-[#D4AF37]/20" data-testid="pricing-commoner">
              <h3 className="text-lg font-semibold text-[#F3F4F6] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Commoner</h3>
              <p className="text-sm text-[#A1A1AA] mb-6">Build your world — free forever</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-[#F3F4F6]">Free</span>
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  "1 Campaign · 50MB Storage",
                  "All Core Entity Types (Story, NPCs, Locations, Buildings, Lore, Maps, Items, Factions, Quests)",
                  "Parchment Character Sheets (PC / NPC / Companion / Creature)",
                  "AI Oracle (names, hooks, descriptions, family tree links)",
                  "SRD Bestiary — 334 creatures with full stat blocks",
                  "SRD Items library",
                  "DM Screen & Quick Reference",
                  "42 Forgotten Realms Deities pre-seeded",
                  "Player Wiki (physical-tome layout) · Multi-tab entity workspace",
                  "Session Notes · Session Recap from audio · Family Trees",
                  "Static Maps + pins + battle notes",
                  "Combat Tracker · Encounter Builder · Dice Roller",
                  "@Mention Auto-Linking · Folder Library · Universal Search (⌘K)",
                ].map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                    <Check className="w-4 h-4 text-[#D4AF37]/60 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Button onClick={() => navigate('/login')} className="w-full bg-transparent text-[#F3F4F6] font-semibold py-3 rounded-full border border-white/20 hover:bg-white/5 hover:border-[#D4AF37]/50 transition-all" data-testid="cta-commoner">
                Start Free
              </Button>
            </div>

            {/* Adventurer — Featured */}
            <div className="rounded-3xl bg-[#1c1917] border border-[#D4AF37] shadow-[0_0_40px_rgba(212,175,55,0.15)] p-8 md:scale-105 md:z-10 relative" data-testid="pricing-adventurer">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#09090b] text-xs font-bold uppercase tracking-wider px-4 py-1 rounded-full">
                Most Popular
              </div>
              <h3 className="text-lg font-semibold text-[#F3F4F6] mb-1 mt-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Adventurer</h3>
              <p className="text-sm text-[#A1A1AA] mb-4">Run live tabletop sessions</p>
              <div className="mb-1">
                <span className="text-4xl font-bold text-[#F3F4F6]">£20</span>
                <span className="text-[#A1A1AA] text-sm">/year</span>
              </div>
              <p className="text-xs text-[#D4AF37]/70 mb-6">Less than £1.70/month</p>
              <ul className="space-y-3 mb-8">
                {[
                  "5 Campaigns · 500MB Storage",
                  "🎲 Full live Virtual Tabletop (tokens · grid · drag-move · spell templates)",
                  "🌫️ Basic Fog of War (DM-painted reveals)",
                  "⚡ Real-time multiplayer (players see moves live)",
                  "🎨 AI Image Generation (portraits · creatures · items · handouts)",
                  "🔗 Player invites (unlimited) + Character URL Import",
                  "📖 Player Wiki bulk-reveal · Just Revealed feed · back-references",
                  "📤 Campaign Export (PDF / DOCX)",
                  "🖼️ Campaign header images",
                  "Everything in Commoner",
                ].map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                    <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Button onClick={() => window.location.href = "https://buy.stripe.com/00w00bdAHdEzgmt65ecs800"} className="w-full bg-[#D4AF37] text-[#09090b] font-bold py-3 rounded-full hover:bg-[#FDE047] transition-all shadow-[0_0_20px_rgba(212,175,55,0.4)]" data-testid="cta-adventurer">
                Choose Adventurer
              </Button>
            </div>

            {/* Archmage */}
            <div className="rounded-2xl bg-[#18181b] border border-white/5 p-8 transition-all duration-300 hover:border-[#D4AF37]/20" data-testid="pricing-archmage">
              <h3 className="text-lg font-semibold text-[#F3F4F6] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Archmage</h3>
              <p className="text-sm text-[#A1A1AA] mb-4">The full Roll20-tier experience</p>
              <div className="mb-1">
                <span className="text-4xl font-bold text-[#F3F4F6]">£30</span>
                <span className="text-[#A1A1AA] text-sm">/year</span>
              </div>
              <p className="text-xs text-[#A1A1AA]/70 mb-6">Less than £2.50/month</p>
              <ul className="space-y-3 mb-8">
                {[
                  "Unlimited Campaigns · 2GB Storage",
                  "🌒 Dynamic Lighting Engine — walls block sight, doors/windows toggle",
                  "👁️ Per-Token Vision — Bright · Dim · Darkvision presets",
                  "🚫 Token-Wall Collision — tokens can't phase through walls",
                  "🎭 Vision Mode toggle (Token Vision ↔ Fog of War, Roll20-style)",
                  "♾️ Unlimited AI session recaps from audio",
                  "🏅 Priority email support · beta feature access",
                  "Everything in Adventurer",
                ].map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                    <Check className="w-4 h-4 text-[#D4AF37]/60 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Button onClick={() => window.location.href = "https://buy.stripe.com/4gMdR10NVasneelgJScs802"} className="w-full bg-transparent text-[#F3F4F6] font-semibold py-3 rounded-full border border-white/20 hover:bg-white/5 hover:border-[#D4AF37]/50 transition-all" data-testid="cta-archmage">
                Choose Archmage
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-24 sm:py-32 relative" data-testid="testimonials-section">
        <div className="absolute inset-0 opacity-[0.08]">
          <img src="https://images.unsplash.com/photo-1676651471150-0e3a5f8de05e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxwZW9wbGUlMjBwbGF5aW5nJTIwZHVuZ2VvbnMlMjBhbmQlMjBkcmFnb25zJTIwYm9hcmQlMjBnYW1lfGVufDB8fHx8MTc3NTAyOTYwN3ww&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09090b] via-[#09090b]/80 to-[#09090b]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">From the Table</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#F3F4F6]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              What Dungeon Masters Are Saying
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                quote: "As someone who's used World Anvil and felt like I was staring at the circuit diagram of a nuclear reactor, I actually prefer this. The dashboard is really clean and easy to navigate, and the DM screen tool is awesome \u2014 creating session prompts for anything is so quick.",
                highlight: "clean and easy to navigate"
              },
              {
                quote: "I LOVE being able to drop pins on maps. Being able to link those directly to NPCs and locations is such a cool feature \u2014 it makes everything feel connected instead of scattered. Honestly one of my favourite parts of the platform.",
                highlight: "feel connected instead of scattered"
              },
              {
                quote: "The Lore section is really well thought out. I added a weapon entry and it actually prompted me to include it in the inventory \u2014 such a small detail but it makes a big difference when organising everything.",
                highlight: "makes a big difference"
              },
              {
                quote: "This feels like it was built by a DM who actually understands what DMs need. It's simple, clean, and doesn't overwhelm you with unnecessary complexity.",
                highlight: "built by a DM who actually understands"
              },
              {
                quote: "Compared to other worldbuilding tools, this is way more intuitive. I can actually focus on my campaign instead of figuring out how to use the platform.",
                highlight: "way more intuitive"
              },
              {
                quote: "The combination of map linking, session tracking, and quick idea generation tools makes this incredibly useful mid-session. It's clearly designed for real gameplay, not just planning.",
                highlight: "designed for real gameplay"
              }
            ].map((t, i) => (
              <div 
                key={i} 
                className="bg-[#18181b]/60 backdrop-blur-xl border border-white/5 rounded-lg p-6 flex flex-col justify-between hover:border-[#D4AF37]/20 transition-colors"
                data-testid={`testimonial-${i}`}
              >
                <div>
                  <div className="flex gap-0.5 mb-4">
                    {[...Array(5)].map((_, s) => <Star key={s} className="w-3.5 h-3.5 fill-[#D4AF37] text-[#D4AF37]" />)}
                  </div>
                  <p className="text-sm text-[#D4D4D8] leading-relaxed mb-4">
                    "{t.quote}"
                  </p>
                </div>
                <p className="text-xs text-[#D4AF37]/70 italic mt-auto">"{t.highlight}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-24 sm:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#F3F4F6] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Ready to Write Your Legend?
          </h2>
          <p className="text-base lg:text-lg text-[#A1A1AA] max-w-lg mx-auto mb-10">
            Free to start. No credit card required. Your first campaign awaits.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/login')}
              data-testid="cta-final"
              className="bg-[#D4AF37] text-[#09090b] font-bold px-10 py-6 rounded-full hover:bg-[#FDE047] transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.4)] hover:shadow-[0_0_30px_rgba(212,175,55,0.6)] text-lg"
            >
              Begin Your Chronicle <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              onClick={() => navigate('/login')}
              data-testid="cta-final-email"
              className="bg-transparent text-[#F3F4F6] font-semibold px-10 py-6 rounded-full border border-white/20 hover:bg-white/5 hover:border-[#D4AF37]/50 transition-all duration-300 text-lg"
            >
              Sign Up with Email
            </Button>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-white/5 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="NaturalOne Chronicles" className="w-8 h-8 rounded-lg object-contain" />
              <span className="text-sm tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700 }}>
                NaturalOne<span className="text-[#D4AF37]">-Chronicles</span>
              </span>
            </div>
            <div className="flex items-center gap-6 text-sm text-[#A1A1AA]">
              <a href="mailto:Naturalone-chronicles@outlook.com" className="hover:text-[#D4AF37] transition-colors" data-testid="landing-contact-email">
                Contact
              </a>
              <span className="text-white/20">|</span>
              <span>Roll for initiative. Your adventure awaits.</span>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-white/5 text-center">
            <p className="text-[10px] text-[#A1A1AA]/50 leading-relaxed max-w-2xl mx-auto" data-testid="srd-attribution">
              This application includes content from the Systems Reference Document 5.2 ("SRD 5.2") by Wizards of the Coast LLC,
              used under the Creative Commons Attribution 4.0 International License. Dungeons & Dragons, D&D, and related
              trademarks are property of Wizards of the Coast LLC. NaturalOne-Chronicles is not affiliated with or endorsed by
              Wizards of the Coast.
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
        .animate-marquee-slow {
          animation: marquee 30s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
